/**
 * Peças visuais do PMIS: selo de status (sempre cor + ícone + rótulo, nunca só cor), barra de progresso, foto da pessoa,
 * cabeçalho de página, estado vazio, ícones e avisos rápidos.
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode, type SVGProps } from 'react';
import clsx from 'clsx';
import { DASHBOARD_STATUS_LABEL, HEALTH_LABEL, PHASE_LABEL, fmtElapsed, todayBR, type DashboardStatus, type Health, type Phase } from '@pmis/shared';
import { fmtDate } from '../lib/format';
import { blobUrl } from '../lib/api';
import { usePrintCover } from '../lib/printCover';

export const STATUS_VAR: Record<DashboardStatus, string> = {
  SAUDAVEL: 'var(--st-saudavel)', ATENCAO: 'var(--st-atencao)', CRITICO: 'var(--st-critico)', ON_HOLD: 'var(--st-onhold)', FALTA_TAP: 'var(--st-faltatap)',
  FALTA_TE: 'var(--st-faltate)', FINALIZADO: 'var(--st-finalizado)', CANCELADO: 'var(--st-cancelado)',
};
const HEALTH_STATUS: Record<Health, DashboardStatus> = { saudavel: 'SAUDAVEL', atencao: 'ATENCAO', critico: 'CRITICO' };

/** Ícone por status: a cor nunca carrega o sentido sozinha. */
export function StatusGlyph({ status, className }: { status: DashboardStatus; className?: string }) {
  const c = clsx('size-3.5 shrink-0', className);
  switch (status) {
    case 'SAUDAVEL': return <svg viewBox="0 0 16 16" className={c} aria-hidden><circle cx="8" cy="8" r="7" fill={STATUS_VAR[status]} /><path d="M4.8 8.2 7 10.3l4.2-4.6" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    case 'ATENCAO': return <svg viewBox="0 0 16 16" className={c} aria-hidden><path d="M8 1.5 15 14H1z" fill={STATUS_VAR[status]} /><path d="M8 6v3.6M8 11.6v.1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" /></svg>;
    case 'CRITICO': return <svg viewBox="0 0 16 16" className={c} aria-hidden><path d="M5 1h6l4 4v6l-4 4H5l-4-4V5z" fill={STATUS_VAR[status]} /><path d="M8 4.5v4.2M8 11.3v.1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" /></svg>;
    case 'ON_HOLD': return <svg viewBox="0 0 16 16" className={c} aria-hidden><circle cx="8" cy="8" r="7" fill={STATUS_VAR[status]} /><path d="M6.2 5v6M9.8 5v6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" /></svg>;
    case 'FALTA_TAP': return <svg viewBox="0 0 16 16" className={c} aria-hidden><rect x="2" y="1" width="12" height="14" rx="2" fill={STATUS_VAR[status]} /><path d="M5 5h6M5 8h6M5 11h3" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" /></svg>;
    case 'FALTA_TE': return <svg viewBox="0 0 16 16" className={c} aria-hidden><rect x="2" y="1" width="12" height="14" rx="2" fill={STATUS_VAR[status]} /><path d="m5 8.5 2 2 4-4.5" stroke="#fff" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    case 'FINALIZADO': return <svg viewBox="0 0 16 16" className={c} aria-hidden><circle cx="8" cy="8" r="7" fill={STATUS_VAR[status]} /><path d="M5 8.3 7 10.2l4-4.4" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    default: return <svg viewBox="0 0 16 16" className={c} aria-hidden><circle cx="8" cy="8" r="7" fill={STATUS_VAR[status]} /><path d="m5.5 5.5 5 5m0-5-5 5" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" /></svg>;
  }
}

export function StatusBadge({ status, className }: { status: DashboardStatus; className?: string }) {
  return (
    <span className={clsx('inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-line bg-panel-2/70 px-2 py-0.5 text-xs font-medium text-fg', className)}>
      <StatusGlyph status={status} />{DASHBOARD_STATUS_LABEL[status]}
    </span>
  );
}

export function HealthBadge({ health, className, suffix }: { health: Health; className?: string; suffix?: ReactNode }) {
  return (
    <span className={clsx('inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium', className)}>
      <StatusGlyph status={HEALTH_STATUS[health]} />{HEALTH_LABEL[health]}{suffix}
    </span>
  );
}

export function PhaseBadge({ phase }: { phase: Phase }) {
  return <span className="inline-flex items-center rounded-md bg-panel-2 px-2 py-0.5 text-xs font-medium text-muted">{PHASE_LABEL[phase]}</span>;
}

/** Tempo em hold: "desde 12/08/2026 · 2 meses e 3 dias" (sem a data, pede para informar em Editar). */
export function HoldSince({ since, short = false }: { since: string | null; short?: boolean }) {
  if (!since) return <span className="text-warn">{short ? 'em hold' : 'data de início não informada (Editar › Em hold desde)'}</span>;
  const time = fmtElapsed(since, todayBR());
  return short ? <span title={`Em hold desde ${fmtDate(since)}`}>há {time}</span> : <span>desde {fmtDate(since)} · {time}</span>;
}

/** Barra de progresso (o mesmo turquesa do painel atual); o número fica fora da barra, sempre legível. */
export function Progress({ value, planned, className, showLabel = true }: { value: number; planned?: number | null; className?: string; showLabel?: boolean }) {
  const v = Math.max(0, Math.min(100, value));
  return (
    <div className={clsx('flex items-center gap-2', className)} title={planned != null ? `Realizado ${Math.round(v)}% · planejado para hoje ${Math.round(planned)}%` : `${Math.round(v)}%`}>
      <div className="relative h-2 min-w-16 flex-1 overflow-hidden rounded-full bg-panel-2" role="progressbar" aria-valuenow={Math.round(v)} aria-valuemin={0} aria-valuemax={100}>
        <div className="h-full rounded-full bg-teal" style={{ width: `${v}%` }} />
        {planned != null ? <div className="absolute top-0 h-full w-0.5 bg-fg/70" style={{ left: `calc(${Math.min(100, planned)}% - 1px)` }} /> : null}
      </div>
      {showLabel ? <span className="num w-10 text-right text-xs font-medium text-fg">{Math.round(v)}%</span> : null}
    </div>
  );
}

const photoCache = new Map<string, Promise<string>>();
export function Avatar({ userId, name, version, size = 32, className }: { userId?: number | null; name: string; version?: string | null; size?: number; className?: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!userId || !version) { setSrc(null); return; }
    const k = `${userId}:${version}`;
    if (!photoCache.has(k)) photoCache.set(k, blobUrl(`/api/v1/users/${userId}/photo?v=${version}`));
    let live = true;
    photoCache.get(k)!.then((u) => { if (live) setSrc(u); }, () => { photoCache.delete(k); });
    return () => { live = false; };
  }, [userId, version]);
  const initials = name.split(/\s+/).filter(Boolean).map((p) => p[0]).slice(0, 2).join('').toUpperCase();
  const hue = [...name].reduce((s, c) => s + c.charCodeAt(0), 0) % 360;
  return src
    ? <img src={src} alt="" className={clsx('shrink-0 rounded-full object-cover ring-2 ring-white/10', className)} style={{ width: size, height: size }} />
    : <span aria-hidden className={clsx('grid shrink-0 place-items-center rounded-full font-semibold text-white ring-2 ring-white/10', className)}
      style={{ width: size, height: size, fontSize: size * 0.38, background: `linear-gradient(135deg, hsl(${hue} 55% 45%), hsl(${(hue + 40) % 360} 60% 35%))` }}>{initials}</span>;
}

export function PageHeader({ title, subtitle, actions, children }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; children?: ReactNode }) {
  // Na impressão, o título da página vai para a capa do modelo da AD Digital (o subtítulo é texto de ajuda da tela).
  usePrintCover(typeof title === 'string' ? title : null);
  return (
    <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {subtitle ? <p className="mt-0.5 text-sm text-muted">{subtitle}</p> : null}
        {children}
      </div>
      {actions ? <div className="no-print flex flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}

export function Empty({ title, children, icon }: { title: string; children?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-line px-6 py-10 text-center">
      <div className="text-muted">{icon ?? <Icon name="inbox" className="size-8" />}</div>
      <div className="font-medium">{title}</div>
      {children ? <div className="max-w-md text-sm text-muted">{children}</div> : null}
    </div>
  );
}

/** Ícones de traço (24×24, 1,8 px), desenhados aqui para não depender de biblioteca. */
const PATHS: Record<string, string> = {
  home: 'M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z',
  folder: 'M3 6a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
  clock: 'M12 7v5l3 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z',
  users: 'M16 19v-1a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v1M9.5 10a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM21 19v-1a4 4 0 0 0-3-3.87M15.5 3.13a3.5 3.5 0 0 1 0 6.75',
  calendar: 'M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zM4 10h16M8 2v4M16 2v4',
  chart: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  report: 'M8 3h8l4 4v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zM8 12h8M8 16h5M15 3v5h5',
  settings: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z',
  check: 'M5 12.5 10 17l9-10', checks: 'M2 12.5 6.5 17 15 7M9.5 15.5 11 17l11-10', clearAll: 'M3 6h12M3 12h12M3 18h7M15 15l6 6M21 15l-6 6',
  plus: 'M12 5v14M5 12h14',
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM21 21l-5-5',
  filter: 'M3 5h18l-7 8v6l-4 2v-8z',
  bell: 'M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0',
  sparkles: 'M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6zM19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8zM5 15l.6 1.4L7 17l-1.4.6L5 19l-.6-1.4L3 17l1.4-.6z',
  inbox: 'M22 12h-6l-2 3h-4l-2-3H2M5.5 5h13L22 12v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-6z',
  download: 'M12 3v12m0 0-4-4m4 4 4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2',
  link: 'M10 14a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1M14 10a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1',
  play: 'M7 4v16l13-8z', stop: 'M6 6h12v12H6z', lock: 'M6 11h12v10H6zM8 11V7a4 4 0 0 1 8 0v4',
  flag: 'M4 22V4m0 0h13l-2 4 2 4H4', meeting: 'M17 20v-2a3 3 0 0 0-3-3H6a3 3 0 0 0-3 3v2M10 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM21 20v-2a3 3 0 0 0-2-2.8M17 3.2a4 4 0 0 1 0 7.6',
  menu: 'M4 6h16M4 12h16M4 18h16', x: 'M6 6l12 12M18 6 6 18', chevron: 'M9 6l6 6-6 6', back: 'M15 6l-6 6 6 6',
  book: 'M4 4.5A2.5 2.5 0 0 1 6.5 2H20v18H6.5A2.5 2.5 0 0 0 4 22.5zM4 19.5A2.5 2.5 0 0 1 6.5 17H20',
  info: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 16v-5M12 8h.01', logout: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
  user: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', moon: 'M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z',
  sun: 'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10zM12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4',
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z', plane: 'M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z',
  edit: 'M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z', trash: 'M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14',
  refresh: 'M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15',
  mail: 'M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM22 6l-10 7L2 6',
  heart: 'M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1.1L12 21l7.8-7.5 1-1.1a5.5 5.5 0 0 0 0-7.8z',
  upload: 'M12 21V9m0 0-4 4m4-4 4 4M4 7V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2',
  copy: 'M9 9h11v11H9zM5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1',
  send: 'M22 2 11 13M22 2l-7 20-4-9-9-4z',
  arrowUp: 'M12 19V5M5 12l7-7 7 7', arrowDown: 'M12 5v14M19 12l-7 7-7-7',
};
export function Icon({ name, className, ...rest }: { name: keyof typeof PATHS | string; className?: string } & SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={clsx('size-5 shrink-0', className)} aria-hidden {...rest}>
      <path d={PATHS[name] ?? PATHS.info} />
    </svg>
  );
}

// Avisos rápidos (canto inferior direito).
interface Toast { id: number; tone: 'ok' | 'err' | 'info'; text: string }
const ToastCtx = createContext<(text: string, tone?: Toast['tone']) => void>(() => {});
export function ToastProvider({ children }: { children: ReactNode }) {
  const [list, setList] = useState<Toast[]>([]);
  const push = useCallback((text: string, tone: Toast['tone'] = 'ok') => {
    const id = Date.now() + Math.random();
    setList((l) => [...l.slice(-3), { id, tone, text }]);
    setTimeout(() => setList((l) => l.filter((t) => t.id !== id)), tone === 'err' ? 8000 : 4000);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="no-print pointer-events-none fixed bottom-4 right-4 z-50 flex w-[min(420px,calc(100%-32px))] flex-col gap-2" aria-live="polite">
        {list.map((t) => (
          <div key={t.id} role={t.tone === 'err' ? 'alert' : 'status'} className={clsx('pointer-events-auto rounded-lg border px-4 py-3 text-sm shadow-lg glass',
            t.tone === 'err' ? 'border-err/50 text-err' : t.tone === 'ok' ? 'border-ok/40 text-fg' : 'border-line text-fg')}>
            {t.text}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx);
